import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { brandGroupIdAtom } from '~/atoms'
import { HStack } from '~/components/elements/HStack'
import { Icon } from '~/components/elements/Icon'
import { VStack } from '~/components/elements/VStack'
import {
  type BrandItem,
  DisplayToggleButton,
} from '~/components/features/DisplayToggleButton'
import { ShareDropDownMenuButton } from '~/components/features/ShareDropDownMenuButton'
import {
  SortButton,
  type SortButtonProps,
} from '~/components/features/SortButton'
import { Table } from '~/components/features/Table'
import { TableContainer } from '~/components/features/TableContainer'
import { AmplitudeProvider } from '~/components/providers/AmplitudeEventProvider'
import { useSurveyQueryAvailableBrandGroups } from '~/hooks/useSurveyQuery'
import { type Brand } from '~/hooks/useSurveyQuery'
import {
  calcHeatmapRatio,
  type ColumnInfo,
  type DisplayColumnItem,
  type DisplayRowItem,
  type FiveNumber,
  fiveSegmentLabelI18nKeyMap,
  formatNineSegment,
  type NineNumber,
  NumFormat,
  type RowInfo,
  type SegmentDisplayType,
  serializeTableToText,
  type SortValue,
  toFiveSegment,
} from '~/utils'

import { segmentDisplayTypeAtom } from '../../atoms'
import { useFigureInfo } from '../../hooks/useFigureInfo'
import { useNpiDriverTableData } from '../../hooks/useNpiDriverTableData'
import { useRestoringParams } from '../../hooks/useRestoringParams'
import { buildNpiDriverTabulationData, MANUAL_URLS } from '../../utils'
import { ManualLink } from '../ManualLink'

import { buildNpiDriverAiSupplement } from './buildNpiDriverAiSupplement'
import { ComparisonAdvantageBrandsCell } from './ComparisonAdvantageBrandsCell'
import styles from './NpiDriverTable.css'

export type ColumnKey =
  | 'answerText'
  | 'comparisonAdvantage'
  | 'comparisonAdvantageBrands'
  | 'fromNpi'
  | 'importance'
  | 'no'
  | 'npiDiff'
  | 'recommendedWhatAxis'
  | 'spacerAfterComparison'
  | 'spacerAfterNineSegs'
  | 'spacerAfterRecommendedWhat'
  | 'toNpi'

const ALL_COLUMNS: ColumnInfo<ColumnKey>[] = [
  { key: 'no', width: '3em' },
  { key: 'answerText', width: 'max-content' },
  { key: 'npiDiff', width: '10em' },
  { key: 'importance', width: '9em' },
  { key: 'spacerAfterNineSegs', width: '0.75em', isSpacer: true },
  { key: 'comparisonAdvantage', width: 'max-content' },
  { key: 'comparisonAdvantageBrands', width: 'fit-content(20em)' },
  { key: 'spacerAfterComparison', width: '0.75em', isSpacer: true },
  { key: 'recommendedWhatAxis', width: '8em' },
  { key: 'spacerAfterRecommendedWhat', width: '0.75em', isSpacer: true },
  { key: 'fromNpi', width: '9em' },
  { key: 'toNpi', width: '9em' },
]

// 列グループ（9segs顧客データ / 参考：競合比較 / 元9segs顧客データ）の定義
type ColumnGroup = {
  labelI18nKey:
    | '9segs_customer_data'
    | 'npi_driver_group_comparison'
    | 'npi_driver_group_original_9segs'
    | 'npi_driver_group_recommended_what'
  columnKeys: ColumnKey[]
}

const COLUMN_GROUPS: ColumnGroup[] = [
  {
    labelI18nKey: '9segs_customer_data',
    columnKeys: ['npiDiff', 'importance'],
  },
  {
    labelI18nKey: 'npi_driver_group_comparison',
    columnKeys: ['comparisonAdvantage', 'comparisonAdvantageBrands'],
  },
  {
    labelI18nKey: 'npi_driver_group_recommended_what',
    columnKeys: ['recommendedWhatAxis'],
  },
  {
    labelI18nKey: 'npi_driver_group_original_9segs',
    columnKeys: ['fromNpi', 'toNpi'],
  },
]

// グループ内の全列が非表示のときは、グループ境界のスペーサー列も表示しない
const calcVisibleColumns = (
  hiddenColumnKeys: ColumnKey[],
): ColumnInfo<ColumnKey>[] => {
  const isGroupVisible = COLUMN_GROUPS.map((group) =>
    group.columnKeys.some((key) => !hiddenColumnKeys.includes(key)),
  )
  // スペーサーは「直前のグループが可視」かつ「後続に可視グループがある」境界にだけ表示する
  // （中間グループが非表示のとき、その前後のスペーサーが2つ並ばないようにする）
  const isSpacerVisible = (groupIndex: number) =>
    isGroupVisible[groupIndex] &&
    isGroupVisible.slice(groupIndex + 1).some(Boolean)

  return ALL_COLUMNS.filter((column) => {
    if (column.key === 'spacerAfterNineSegs') {
      return isSpacerVisible(0)
    }
    if (column.key === 'spacerAfterComparison') {
      return isSpacerVisible(1)
    }
    if (column.key === 'spacerAfterRecommendedWhat') {
      return isSpacerVisible(2)
    }

    return !hiddenColumnKeys.includes(column.key)
  })
}

// 列ヘッダーのラベルと、マニュアルへ遷移する？マークリンク。
// ?は押下時にマニュアルを別タブで開くのみで、ツールチップ（title）は表示しない。
const HeaderLabelWithHelp = ({
  href,
  label,
  underlineFirstLine,
}: {
  label: string
  href: string
  underlineFirstLine?: boolean
}) => {
  // 下線を付ける1行目と、それ以降（改行を含む残りの文字列）に分ける
  const [firstLine] = label.split('\n')

  return (
    <span className={styles.headerLabelWithHelp}>
      <span className={styles.headerLabel}>
        {underlineFirstLine ? (
          <>
            <span className={styles.headerLabelFirstLine}>{firstLine}</span>
            {label.slice(firstLine.length)}
          </>
        ) : (
          label
        )}
      </span>
      <ManualLink className={styles.helpLink} href={href}>
        <span
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {label}
        </span>
        <Icon aria-hidden="true" icon="help" size="1rem" />
      </ManualLink>
    </span>
  )
}

// 競合に対する優位性列のヘッダー。対象ブランド列の表示切り替えボタン付き
const ToggleColumnHeader = ({
  isBrandsColumnVisible,
  label,
  onToggle,
}: {
  isBrandsColumnVisible: boolean
  label: string
  onToggle: () => void
}) => {
  const { t } = useTranslation()

  return (
    <div className={styles.toggleColumnHeader}>
      <HeaderLabelWithHelp
        href={MANUAL_URLS.comparisonAdvantageColumnHeader}
        label={label}
        underlineFirstLine={true}
      />
      <button
        className={styles.columnToggleButton}
        type="button"
        onClick={onToggle}
      >
        {isBrandsColumnVisible ? t('hide_column') : t('show_column')}
      </button>
    </div>
  )
}

export type NpiDriverTableProps = {
  brand: Brand
  fromSegmentNumber: number
  toSegmentNumber: number
  commentValue: string
  // saveDocumentImage の保存に使う restoringParams。commentRef を含むため、
  // コメントブロック側の保存と同一の restoreKey になるよう親から渡す。
  saveRestoringParams: ReturnType<typeof useRestoringParams>
  onCommentSaved: (saved: string) => void
  // AIコメント生成の入力（遷移ルート行＋表示中ビューのタブ区切りテキスト）の通知。
  // 生成ボタンは親（Outputs）のコメントブロック側にあり、テーブルのビュー状態
  // （ソート・表示列/行）はこのコンポーネントが持つため、テキスト化結果を親へ渡す。
  onTabulationDataChange?: (tabulationData: string) => void
}

// NPIドライバーテーブルの行データを構築するhook
// 引数の取り違えを型エラーで検出できるよう、オブジェクト引数で受け取る
const useTableInfo = ({
  fromHeaderLabel,
  fromSegmentNumber,
  hiddenBrandNames,
  hiddenColumnKeys,
  hiddenRowIds,
  onToggleComparisonAdvantageBrandsColumn,
  segmentDisplayType,
  sortValue,
  toHeaderLabel,
  toSegmentNumber,
}: {
  fromHeaderLabel: string
  fromSegmentNumber: number
  hiddenBrandNames: string[]
  hiddenColumnKeys: ColumnKey[]
  hiddenRowIds: number[]
  onToggleComparisonAdvantageBrandsColumn: () => void
  segmentDisplayType: SegmentDisplayType
  sortValue: SortValue<ColumnKey> | null
  toHeaderLabel: string
  toSegmentNumber: number
}): RowInfo<ColumnKey>[] => {
  const { t } = useTranslation()
  const allData = useNpiDriverTableData()

  return React.useMemo(() => {
    const isComparisonAdvantageBrandsVisible = !hiddenColumnKeys.includes(
      'comparisonAdvantageBrands',
    )

    const dataWithRaw = allData
      .filter((row) => !hiddenRowIds.includes(row.option))
      .map((row) => {
        const npiRatioBySegment =
          segmentDisplayType === 'fiveSegs'
            ? row.npiRatioByFiveSegment
            : row.npiRatioByNineSegment
        const importanceRatioBySegment =
          segmentDisplayType === 'fiveSegs'
            ? row.importanceRatioByFiveSegment
            : row.importanceRatioByNineSegment
        const fromNpi = npiRatioBySegment[fromSegmentNumber] ?? 0
        const toNpi = npiRatioBySegment[toSegmentNumber] ?? 0
        const npiDiff = toNpi - fromNpi
        const importanceRatio = importanceRatioBySegment[fromSegmentNumber] ?? 0

        const visibleAdvantageBrands = row.comparisonAdvantageBrands.filter(
          (brand) => !hiddenBrandNames.includes(brand.name),
        )

        return {
          ...row,
          fromNpi,
          importanceRatio,
          npiDiff,
          toNpi,
          visibleAdvantageBrands,
        }
      })

    const npiDiffs = dataWithRaw.map((d) => d.npiDiff)
    const maxNpiDiff = npiDiffs.length > 0 ? Math.max(...npiDiffs) : 0
    const minNpiDiff = npiDiffs.length > 0 ? Math.min(...npiDiffs) : 0

    // 推奨WHAT軸の✓判定: 旧Index（重視点×NPIドライバー差分）top3 と
    // PoDスコア top3 の和集合（表示中の行のみが母集団、スコア0以下は対象外）。
    // 旧Indexは「平均で正規化した倍率」だったが、順位は正規化前のスコア積と一致するため積で判定する。
    const topThreeOptions = (
      scores: { option: number; score: number }[],
    ): number[] =>
      scores
        .filter((s) => s.score > 0)
        .sort((a, b) => b.score - a.score || a.option - b.option)
        .slice(0, 3)
        .map((s) => s.option)

    const recommendedWhatAxisOptions = new Set<number>([
      ...topThreeOptions(
        dataWithRaw.map((d) => ({
          option: d.option,
          score: d.importanceRatio * d.npiDiff,
        })),
      ),
      ...topThreeOptions(
        dataWithRaw.map((d) => ({ option: d.option, score: d.podScore ?? 0 })),
      ),
    ])
    const maxAdvantageCount = Math.max(
      0,
      ...dataWithRaw.map((d) => d.visibleAdvantageBrands.length),
    )

    const sorted = [...dataWithRaw].sort((a, b) => {
      // ソート未選択時は選択肢番号（No.）昇順で表示する
      if (sortValue === null) {
        return a.option - b.option
      }

      const diff =
        sortValue.columnKey === 'importance'
          ? a.importanceRatio - b.importanceRatio
          : a.npiDiff - b.npiDiff

      return sortValue.ascending ? diff : -diff
    })

    // 1行目: 列グループ（9segs顧客データ / 競合比較 / 推奨WHAT軸 / 元9segs顧客データ）のラベル行
    // 左上の空セルはグループラベル行と列ヘッダー行の2行にまたがる1つのセルにする
    const groupHeaderRow: RowInfo<ColumnKey> = {
      no: { isColumnHeader: true, colSpan: 2, rowSpan: 2 },
    }
    for (const group of COLUMN_GROUPS) {
      const visibleKeys = group.columnKeys.filter(
        (key) => !hiddenColumnKeys.includes(key),
      )
      const firstVisibleKey = visibleKeys[0]
      if (firstVisibleKey === undefined) {
        continue
      }
      // 推奨WHAT軸は単一列のため、グループ見出し行と列ヘッダー行を1セルに結合（2行スパン）する
      const isRecommendedWhat =
        group.labelI18nKey === 'npi_driver_group_recommended_what'
      groupHeaderRow[firstVisibleKey] = {
        align: 'center',
        colSpan: visibleKeys.length,
        isColumnHeader: true,
        isHeader: true,
        ...(isRecommendedWhat
          ? {
              rowSpan: 2,
              copyValue: t(group.labelI18nKey),
              node: (
                <HeaderLabelWithHelp
                  href={MANUAL_URLS.recommendedWhatAxisColumnHeader}
                  label={t(group.labelI18nKey)}
                />
              ),
            }
          : { text: t(group.labelI18nKey) }),
      }
    }

    const headerRow: RowInfo<ColumnKey> = {
      npiDiff: {
        isHeader: true,
        isColumnHeader: true,
        text: `${fromHeaderLabel}\n→${toHeaderLabel}\n${t('npi_driver_table_npi_driver_diff')}`,
        align: 'center',
      },
      importance: {
        isHeader: true,
        isColumnHeader: true,
        text: `${fromHeaderLabel}\n${t('npi_driver_table_importance')}`,
        align: 'center',
      },
      comparisonAdvantage: {
        isHeader: true,
        isColumnHeader: true,
        copyValue: t('comparison_advantage'),
        node: (
          <ToggleColumnHeader
            isBrandsColumnVisible={isComparisonAdvantageBrandsVisible}
            label={t('comparison_advantage_header')}
            onToggle={onToggleComparisonAdvantageBrandsColumn}
          />
        ),
        align: 'center',
      },
      comparisonAdvantageBrands: {
        isHeader: true,
        isColumnHeader: true,
        text: t('comparison_advantage_brands_column'),
        align: 'center',
      },
      fromNpi: {
        isHeader: true,
        isColumnHeader: true,
        text: `${fromHeaderLabel}\n${t('npi_driver_table_npi_driver')}`,
        align: 'center',
      },
      toNpi: {
        isHeader: true,
        isColumnHeader: true,
        text: `${toHeaderLabel}\n${t('npi_driver_table_npi_driver')}`,
        align: 'center',
      },
    }

    const dataRows: RowInfo<ColumnKey>[] = sorted.map((row) => {
      const [npiDiffPts, npiDiffCopyValue] = NumFormat.ratioToPts(row.npiDiff)
      const advantageCount = row.visibleAdvantageBrands.length

      return {
        no: { text: row.option, align: 'center' },
        answerText: { text: row.text, textCharacterLimit: 30, align: 'left' },
        npiDiff: {
          ratio: calcHeatmapRatio(row.npiDiff, maxNpiDiff, minNpiDiff),
          heatmap: true,
          text: npiDiffPts,
          copyValue: npiDiffCopyValue,
          align: 'right',
        },
        importance: {
          ratio: row.importanceRatio,
          heatmap: true,
          text: NumFormat.forRatio(row.importanceRatio),
          align: 'right',
        },
        recommendedWhatAxis: {
          ...(recommendedWhatAxisOptions.has(row.option)
            ? { heatmap: true, ratio: 1, text: '✓' }
            : {}),
          align: 'center',
        },
        comparisonAdvantage: {
          heatmap: true,
          ratio: maxAdvantageCount > 0 ? advantageCount / maxAdvantageCount : 0,
          // クリップボード上で数値として扱えるよう、コピーには件数のみを使う
          text:
            advantageCount > 0
              ? t('npi_driver_brands_count', { count: advantageCount })
              : '',
          copyValue: advantageCount > 0 ? String(advantageCount) : '',
          align: 'center',
        },
        comparisonAdvantageBrands: {
          node: (
            <ComparisonAdvantageBrandsCell
              brands={row.visibleAdvantageBrands}
            />
          ),
          copyValue: row.visibleAdvantageBrands.map((b) => b.name).join(', '),
          align: 'left',
        },
        fromNpi: {
          ratio: row.fromNpi,
          heatmap: true,
          text: NumFormat.forRatio(row.fromNpi),
          align: 'right',
        },
        toNpi: {
          ratio: row.toNpi,
          heatmap: true,
          text: NumFormat.forRatio(row.toNpi),
          align: 'right',
        },
      }
    })

    return [groupHeaderRow, headerRow, ...dataRows]
  }, [
    allData,
    fromHeaderLabel,
    fromSegmentNumber,
    hiddenBrandNames,
    hiddenColumnKeys,
    hiddenRowIds,
    onToggleComparisonAdvantageBrandsColumn,
    segmentDisplayType,
    sortValue,
    t,
    toHeaderLabel,
    toSegmentNumber,
  ])
}

// 矢印選択時に表示するNPIドライバー分析テーブル
export const NpiDriverTable = ({
  brand,
  commentValue,
  fromSegmentNumber,
  onCommentSaved,
  onTabulationDataChange,
  saveRestoringParams,
  toSegmentNumber,
}: NpiDriverTableProps) => {
  const { t } = useTranslation()
  const segmentDisplayType = useAtomValue(segmentDisplayTypeAtom)
  const allData = useNpiDriverTableData()

  // 9segsの場合、ヘッダーに番号付きセグメント名を表示する
  // 5segsには番号がないから付けない
  const fromHeaderLabel =
    segmentDisplayType === 'nineSegs'
      ? formatNineSegment(fromSegmentNumber as NineNumber, '%n. %t1', t)
      : t(
          fiveSegmentLabelI18nKeyMap[
            toFiveSegment(fromSegmentNumber as FiveNumber)
          ],
        )
  const toHeaderLabel =
    segmentDisplayType === 'nineSegs'
      ? formatNineSegment(toSegmentNumber as NineNumber, '%n. %t1', t)
      : t(
          fiveSegmentLabelI18nKeyMap[
            toFiveSegment(toSegmentNumber as FiveNumber)
          ],
        )

  // 初期表示はNPIドライバー差分の降順でソートする
  const [sortValue, setSortValue] = React.useState<SortValue<ColumnKey> | null>(
    { ascending: false, columnKey: 'npiDiff' },
  )

  const [hiddenColumnKeys, setHiddenColumnKeys] = React.useState<ColumnKey[]>([
    'comparisonAdvantageBrands',
    'fromNpi',
    'toNpi',
  ])

  const [hiddenBrandNames, setHiddenBrandNames] = React.useState<string[]>([])

  const autoHiddenRowIds = React.useMemo(
    () => allData.filter((d) => d.hasTextMismatch).map((d) => d.option),
    [allData],
  )
  const [manualHiddenRowIds, setManualHiddenRowIds] = React.useState<
    number[] | null
  >(null)
  const hiddenRowIds = manualHiddenRowIds ?? autoHiddenRowIds

  const toggleComparisonAdvantageBrandsColumn = React.useCallback(() => {
    setHiddenColumnKeys((prev) =>
      prev.includes('comparisonAdvantageBrands')
        ? prev.filter((k) => k !== 'comparisonAdvantageBrands')
        : [...prev, 'comparisonAdvantageBrands'],
    )
  }, [])

  const brandGroups = useSurveyQueryAvailableBrandGroups()
  const brandGroupId = useAtomValue(brandGroupIdAtom)

  const allCompetitorBrands = React.useMemo<string[]>(() => {
    const presentBrandNames = new Set<string>()
    allData.forEach((row) => {
      row.comparisonAdvantageBrands.forEach((b) =>
        presentBrandNames.add(b.name),
      )
    })

    return (
      brandGroups
        .find((g) => g.id === brandGroupId)
        ?.brands.map((b) => b.name)
        .filter((name) => presentBrandNames.has(name)) ?? []
    )
  }, [allData, brandGroupId, brandGroups])

  const columns = React.useMemo(
    () => calcVisibleColumns(hiddenColumnKeys),
    [hiddenColumnKeys],
  )

  // スペーサー列はクリップボードコピーに含めない
  const columnsForCopy = React.useMemo(
    () => columns.filter((col) => !col.isSpacer),
    [columns],
  )

  const tableInfo = useTableInfo({
    fromHeaderLabel,
    fromSegmentNumber,
    hiddenBrandNames,
    hiddenColumnKeys,
    hiddenRowIds,
    onToggleComparisonAdvantageBrandsColumn:
      toggleComparisonAdvantageBrandsColumn,
    segmentDisplayType,
    sortValue,
    toHeaderLabel,
    toSegmentNumber,
  })

  // データなしのとき（テーブル非表示）は空文字を通知し、親側で生成をスキップさせる
  const tabulationData = React.useMemo(() => {
    if (allData.length === 0) {
      return ''
    }

    const serializedTable = serializeTableToText(columnsForCopy, tableInfo)
    // 全列非表示などで表本体が空になったら、ルート行＋補足だけでAI生成させない（データなし扱い）。
    // 親側は空文字を受け取ると生成をスキップする。
    if (serializedTable.trim() === '') {
      return ''
    }

    // 表の列は増やさず、AI分析用の補足（選択肢別 Index・PoDスコア）だけを
    // テキスト末尾に足す。母集団は表示中の行（hiddenRowIds を除外）にそろえる。
    const aiSupplement = buildNpiDriverAiSupplement({
      fromSegmentNumber,
      rows: allData.filter((row) => !hiddenRowIds.includes(row.option)),
      segmentDisplayType,
      t,
      toSegmentNumber,
    })

    return buildNpiDriverTabulationData({
      fromHeaderLabel,
      routeLabel: t('who_and_what_transition_route'),
      serializedTable: aiSupplement
        ? `${serializedTable}\n${aiSupplement}`
        : serializedTable,
      toHeaderLabel,
    })
  }, [
    allData,
    columnsForCopy,
    fromHeaderLabel,
    fromSegmentNumber,
    hiddenRowIds,
    segmentDisplayType,
    t,
    tableInfo,
    toHeaderLabel,
    toSegmentNumber,
  ])

  React.useEffect(() => {
    onTabulationDataChange?.(tabulationData)
  }, [onTabulationDataChange, tabulationData])

  const figureInfo = useFigureInfo({
    brand,
    fromSegmentNumber,
    toSegmentNumber,
  })
  // コメントを持たない copy / share 用の base params（commentRef なし）。
  // 保存（saveDocumentImage）は commentRef を含む saveRestoringParams を使う。
  const restoringParams = useRestoringParams(
    fromSegmentNumber,
    toSegmentNumber,
    'npi-driver',
  )
  const image = <Table columns={columns} tableInfo={tableInfo} />

  const sortButtonItems = React.useMemo<SortButtonProps<ColumnKey>['items']>(
    () => [
      {
        columnKey: 'npiDiff' as const,
        label: `${fromHeaderLabel}→${toHeaderLabel} ${t('npi_driver_table_npi_driver_diff')}`,
      },
      {
        columnKey: 'importance' as const,
        label: `${fromHeaderLabel} ${t('npi_driver_table_importance')}`,
      },
    ],
    [fromHeaderLabel, t, toHeaderLabel],
  )

  const displayColumnItems = React.useMemo<DisplayColumnItem<ColumnKey>[]>(
    () => [
      {
        columnKey: 'npiDiff',
        isVisible: !hiddenColumnKeys.includes('npiDiff'),
        label: `${fromHeaderLabel}→${toHeaderLabel} ${t('npi_driver_table_npi_driver_diff')}`,
      },
      {
        columnKey: 'importance',
        isVisible: !hiddenColumnKeys.includes('importance'),
        label: `${fromHeaderLabel} ${t('npi_driver_table_importance')}`,
      },
      {
        columnKey: 'comparisonAdvantage',
        isVisible: !hiddenColumnKeys.includes('comparisonAdvantage'),
        label: t('comparison_advantage'),
      },
      {
        columnKey: 'comparisonAdvantageBrands',
        isVisible: !hiddenColumnKeys.includes('comparisonAdvantageBrands'),
        label: t('comparison_advantage_brands_column'),
      },
      {
        columnKey: 'recommendedWhatAxis',
        isVisible: !hiddenColumnKeys.includes('recommendedWhatAxis'),
        label: t('npi_driver_group_recommended_what'),
      },
      {
        columnKey: 'fromNpi',
        isVisible: !hiddenColumnKeys.includes('fromNpi'),
        label: `${fromHeaderLabel} ${t('npi_driver_table_npi_driver')}`,
      },
      {
        columnKey: 'toNpi',
        isVisible: !hiddenColumnKeys.includes('toNpi'),
        label: `${toHeaderLabel} ${t('npi_driver_table_npi_driver')}`,
      },
    ],
    [fromHeaderLabel, hiddenColumnKeys, t, toHeaderLabel],
  )

  const displayRowItems = React.useMemo<DisplayRowItem<number>[]>(
    () => [
      {
        label: t('npi_driver_table_answer'),
        subItems: allData.map((row) => ({
          id: row.option,
          isVisible: !hiddenRowIds.includes(row.option),
          label: row.text,
        })),
      },
    ],
    [allData, hiddenRowIds, t],
  )

  const brandItems = React.useMemo<BrandItem[]>(
    () =>
      allCompetitorBrands.map((brand) => ({
        brandName: brand,
        isVisible: !hiddenBrandNames.includes(brand),
      })),
    [allCompetitorBrands, hiddenBrandNames],
  )

  if (allData.length === 0) {
    return null
  }

  return (
    // analysisName を指定すると "Turn onoff_npi_driver npi_driver" のように語が重複する。
    <AmplitudeProvider eventNameSuffix="_npi_driver">
      <VStack align="start" spacing={1}>
        <TableContainer
          header={
            <VStack align="end" spacing={1}>
              <ManualLink
                className={styles.interpretationNotesLink}
                href={MANUAL_URLS.interpretationNotes}
                logEventName="View data interpretation notes"
              >
                {t('npi_driver_interpretation_notes')}
              </ManualLink>

              <HStack spacing={2}>
                <SortButton
                  items={sortButtonItems}
                  value={sortValue}
                  onChange={setSortValue}
                />

                <DisplayToggleButton
                  brandItems={brandItems}
                  columnItems={displayColumnItems}
                  rowItems={displayRowItems}
                  onHiddenBrandChange={setHiddenBrandNames}
                  onHiddenColumnChange={setHiddenColumnKeys}
                  onHiddenRowChange={setManualHiddenRowIds}
                />

                <AmplitudeProvider
                  analysisName="npi_driver"
                  eventNameSuffix={
                    segmentDisplayType === 'fiveSegs'
                      ? '_npi_driver_5segs'
                      : '_npi_driver_9segs'
                  }
                >
                  <ShareDropDownMenuButton
                    copyToClipboard={{
                      columns: columnsForCopy,
                      figureInfo,
                      restoringParams,
                      tableInfo,
                    }}
                    downloadAsPng={{
                      image,
                      name: `${segmentDisplayType === 'fiveSegs' ? '5segs' : '9segs'}-table`,
                    }}
                    getSharedUrl={{ restoringParams }}
                    saveDocumentImage={{
                      commentReadOnly: true,
                      figureInfo,
                      image,
                      initialComment: commentValue,
                      onSaved: onCommentSaved,
                      restoringParams: saveRestoringParams,
                    }}
                  />
                </AmplitudeProvider>
              </HStack>
            </VStack>
          }
          id="npi-driver"
        >
          <Table columns={columns} tableInfo={tableInfo} />
        </TableContainer>
      </VStack>
    </AmplitudeProvider>
  )
}
